import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { stockQuantityLocationService } from 'features/inventory/stock/api/stock-quantity-location.service';
import { stockQuantityService } from 'features/inventory/stock/api/stock-quantity.service';
import { ITransportActiveDocument } from 'features/inventory/transport-documents/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';

interface UpdateTransportActiveDocumentLineDialogFormParams {
  trasportActiveDocumentLine: any;
  ddt: ITransportActiveDocument;
  onSubmit: any;
}

// Function component to update contract line details
function UpdateTransportActiveDocumentLineDialog(
  params: UpdateTransportActiveDocumentLineDialogFormParams
) {
  const { t } = useTranslation();
  const { trasportActiveDocumentLine, ddt, onSubmit } = params;
  const [batchesOption, setBatchesOptions] = useState<Option[]>([]);
  const [_, setSelectedBatch] = useState<Option | null>(null);

  // Calculate the batches options
  useEffect(() => {
    const fetchBatchData = async () => {
      const params = {
        product__id: trasportActiveDocumentLine?.product ?? '',
        warehouse__id: ddt.warehouse_out ?? '',
        quantity__gt: 0
      };

      try {
        // Call both APIs concurrently
        const [stockQuantityRes, stockQuantityLocationRes] = await Promise.all([
          stockQuantityService.getUnloadStocks(1000, 0, params),
          stockQuantityLocationService.getUnloadStockLocation(1000, 0, params)
        ]);

        // Process the first API response
        const firstBatchData = stockQuantityRes.results.map((value) => {
          let description = value.product_batch;
          if (value.expiration_date) description += `; Scad: ${value.expiration_date}`;
          description += `; Qty: ${value.free_quantity}`;
          return {
            id: crypto.randomUUID(),
            code: description,
            product_batch: value.product_batch,
            expiration_date: value.expiration_date,
            qty: value.free_quantity
          };
        });

        // Process the second API response
        const secondBatchData = stockQuantityLocationRes.results.map((value) => {
          let description = value.product_batch;
          if (value.expiration_date) description += `; Scad: ${value.expiration_date}`;
          description += `; Qty: ${value.quantity}; ${value.location__code}`;
          return {
            id: crypto.randomUUID(),
            code: description,
            product_batch: value.product_batch,
            expiration_date: value.expiration_date,
            location: value.location__code,
            qty: value.quantity
          };
        });

        // Combine both results and update state
        setBatchesOptions([...firstBatchData, ...secondBatchData]);
      } catch (error) {
        toast.error(error);
      }
    };

    fetchBatchData();
  }, []);

  // Translation prefix for easier access to translation keys DA METTERE
  const translationPrefix = 'pages.transport-active-document-line.edit';

  // Function to handle form submission
  const onSubmitTrasportActiveDocumentLine = async (values) => {
    const params = {
      product_batch: values['product_batch'].product_batch ?? 'NO_BATCH',
      expiration_date: values['product_batch'].expiration_date,
      location: values['product_batch']?.location ?? '',
      notes: values['product_batch']?.location ?? '',
      quantity: values['quantity']
    };
    onSubmit(trasportActiveDocumentLine.id, params);
  };

  return (
    <EnhancedForm
      onSubmit={onSubmitTrasportActiveDocumentLine}
      formId="updateTransportActiveDocumentLine"
      autoStyleLayout={true}
      columns={2}
      hideControls>
      <IdentityField
        name="product_batch"
        label={t(`${translationPrefix}.product_batch`)}
        component={AutocompleteAdapter}
        options={batchesOption}
        type="text"
        required
        setValue={setSelectedBatch}
        submitObject
        initValue={batchesOption.find((b) => b.id === trasportActiveDocumentLine.product_batch)}
      />
      <IdentityField
        component={TextFieldAdapter}
        name="quantity"
        label={t('global.quantity')}
        type="text"
        defaultValue={trasportActiveDocumentLine.quantity}
      />
    </EnhancedForm>
  );
}

export default UpdateTransportActiveDocumentLineDialog;
